import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { LoginComponent } from './login/login.component';
import { AuthGuard } from './guards/auth.guard';
import { LoginGuard } from './guards/login.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/warehouse-dashboard',
    pathMatch: 'full'
  },
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [LoginGuard]
  },
  {
    path: 'warehouse-dashboard',
    loadComponent: () => import('./pages/warehouse-dashboard/warehouse-dashboard.component').then(m => m.WarehouseDashboardComponent),
    canActivate: [AuthGuard]
  },

  // ── Devices ──────────────────────────────────────────────────────────
  {
    path: 'assets',
    loadComponent: () => import('./pages/devices/devices.component').then(m => m.DevicesComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'expected-deliveries',
    loadComponent: () => import('./pages/expecting-assets/expecting-assets.component').then(m => m.ExpectingAssetsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'scan-in-devices',
    loadComponent: () => import('./pages/receiving-assets/receiving-assets.component').then(m => m.ReceivingAssetsComponent),
    canActivate: [AuthGuard]
  },

  // ── Quality check ────────────────────────────────────────────────────
  {
    path: 'qc-upload',
    loadComponent: () => import('./pages/qc-check/qc-check.component').then(m => m.QcCheckComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'cosmetic-scope',
    loadComponent: () => import('./pages/cosmetic-scope/cosmetic-scope.component').then(m => m.CosmeticScopeComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'qc-discrepancies',
    loadComponent: () => import('./pages/qc-discrepancies/qc-discrepancies.component').then(m => m.QcDiscrepanciesComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'device-timeline/:id',
    loadComponent: () => import('./pages/device-timeline/device-timeline.component').then(m => m.DeviceTimelineComponent),
    canActivate: [AuthGuard]
  },

  // ── Repair batches (create / list / detail — reconcile is inline on detail) ──
  {
    path: 'batches',
    loadComponent: () => import('./pages/batches/batch-list.component').then(m => m.BatchListComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'batches/new',
    loadComponent: () => import('./pages/batches/create-batch/create-batch.component').then(m => m.CreateBatchComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'batches/:id',
    loadComponent: () => import('./pages/batches/batch-detail/batch-detail.component').then(m => m.BatchDetailComponent),
    canActivate: [AuthGuard]
  },

  // ── Executive dashboard (role-gated) ─────────────────────────────────
  {
    path: 'reports/rework',
    loadComponent: () => import('./pages/rework-report/rework-report.component').then(m => m.ReworkReportComponent),
    canActivate: [AuthGuard],
    data: { roles: ['SysAdmin', 'WarehouseManager', 'Executive'] }
  },
  {
    path: 'executive-dashboard',
    loadComponent: () => import('./pages/executive-dashboard/executive-dashboard.component').then(m => m.ExecutiveDashboardComponent),
    canActivate: [AuthGuard],
    data: { roles: ['Executive', 'SysAdmin'] }
  },

  {
    path: 'technician-part-loans',
    loadComponent: () => import('./pages/technician-parts-management/technician-parts-management.component').then(m => m.TechnicianPartsManagementComponent),
    canActivate: [AuthGuard]
  },

  // ── Ship ─────────────────────────────────────────────────────────────
  {
    path: 'create-shipment',
    loadComponent: () => import('./pages/create-shipment/create-shipment.component').then(m => m.CreateShipmentComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'shipments',
    loadComponent: () => import('./pages/shipments/shipments-list.component').then(m => m.ShipmentsListComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'shipments/:id',
    loadComponent: () => import('./pages/shipments/shipment-detail.component').then(m => m.ShipmentDetailComponent),
    canActivate: [AuthGuard]
  },

  // ── Invoicing ────────────────────────────────────────────────────────
  {
    path: 'invoicing/settings',
    loadComponent: () => import('./pages/invoicing/warehouse-settings/warehouse-settings.component').then(m => m.WarehouseSettingsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'invoicing/invoices',
    loadComponent: () => import('./pages/invoicing/invoice-list/invoice-list.component').then(m => m.InvoiceListComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'invoicing/create',
    loadComponent: () => import('./pages/invoicing/create-invoice/create-invoice.component').then(m => m.CreateInvoiceComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'invoicing/invoice/:id',
    loadComponent: () => import('./pages/invoicing/invoice-detail/invoice-detail.component').then(m => m.InvoiceDetailComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'invoicing/client-pricing',
    loadComponent: () => import('./pages/invoicing/client-pricing/client-pricing.component').then(m => m.ClientPricingComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'invoicing/fault-codes',
    loadComponent: () => import('./pages/invoicing/fault-codes/fault-codes.component').then(m => m.FaultCodesComponent),
    canActivate: [AuthGuard]
  },

  // ── Setup ────────────────────────────────────────────────────────────
  {
    path: 'clients',
    loadComponent: () => import('./pages/clients/clients.component').then(m => m.ClientsComponent),
    canActivate: [AuthGuard]
  },
  {
    // The per-client device page was merged into /assets, where the client is just a
    // filter. Redirect rather than drop it: the Clients page links here, and so do
    // existing bookmarks.
    path: 'clients/:id/assets',
    redirectTo: (route) => `/assets?clientId=${route.params['id']}`
  },
  {
    path: 'manage-technicians',
    loadComponent: () => import('./pages/manage-technicians/manage-technicians.component').then(m => m.ManageTechniciansComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'teams',
    loadComponent: () => import('./pages/teams/teams.component').then(m => m.TeamsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'receive-parts',
    loadComponent: () => import('./pages/simple-inventory-receiving/simple-inventory-receiving.component').then(m => m.SimpleInventoryReceivingComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'part-receiving-history',
    loadComponent: () => import('./pages/inventory-receiving-sessions/inventory-receiving-sessions.component').then(m => m.InventoryReceivingSessionsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'inventory-status',
    loadComponent: () => import('./pages/inventory-status/inventory-status.component').then(m => m.InventoryStatusComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'inventory-history',
    loadComponent: () => import('./pages/inventory-history/inventory-history.component').then(m => m.InventoryHistoryComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'discard-parts',
    loadComponent: () => import('./pages/discard-parts/discard-parts.component').then(m => m.DiscardPartsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'return-unused-parts',
    loadComponent: () => import('./pages/return-unused-parts/return-unused-parts.component').then(m => m.ReturnUnusedPartsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'skus',
    loadComponent: () => import('./pages/skus/skus.component').then(m => m.SkusComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'device-models',
    loadComponent: () => import('./pages/device-models/device-models.component').then(m => m.DeviceModelsComponent),
    canActivate: [AuthGuard],
    data: { role: 'SysAdmin' }
  },
  {
    path: 'change-serial-status',
    loadComponent: () => import('./pages/change-serial-status/change-serial-status.component').then(m => m.ChangeSerialStatusComponent),
    canActivate: [AuthGuard]
  },

  // ── Legacy path redirects (keep old bookmarks working) ───────────────
  { path: 'expecting-assets', redirectTo: '/expected-deliveries', pathMatch: 'full' },
  { path: 'receiving-assets', redirectTo: '/scan-in-devices', pathMatch: 'full' },
  { path: 'assign-technician', redirectTo: '/batches/new', pathMatch: 'full' },
  { path: 'create-repair-batch', redirectTo: '/batches/new', pathMatch: 'full' },
  { path: 'add-parts-to-device', redirectTo: '/batches', pathMatch: 'full' },
  { path: 'assign-extra-parts', redirectTo: '/batches', pathMatch: 'full' },
  { path: 'team-batch-management', redirectTo: '/batches', pathMatch: 'full' },
  { path: 'repair-batches', redirectTo: '/batches', pathMatch: 'full' },
  { path: 'technician-parts', redirectTo: '/technician-part-loans', pathMatch: 'full' },
  { path: 'simple-inventory-receiving', redirectTo: '/receive-parts', pathMatch: 'full' },
  { path: 'inventory-receiving-sessions', redirectTo: '/part-receiving-history', pathMatch: 'full' },
  {
    path: 'inventory/low-stock',
    redirectTo: () => {
      const router = inject(Router);
      return router.createUrlTree(['/inventory-status'], { queryParams: { lowStock: 'true' } });
    }
  },

  {
    path: '**',
    redirectTo: '/warehouse-dashboard'
  }
];
